/**
 * Pure H OS · Phase D · P0-5 · Owner Cockpit
 *
 * The first screen the company owner opens every morning.
 * NOT the Executive Dashboard — they are two different experiences:
 *
 *   Executive Dashboard  →  the analytics surface (KPIs deep, morning
 *                           brief cards, department health, revenue
 *                           chart, activity timeline).
 *   Owner Cockpit        →  the ACTION surface — what needs the owner's
 *                           attention this morning, and one-click access
 *                           to the things a founder actually does at
 *                           09:00: approve, decide, brief-review, jump
 *                           into a module.
 *
 * Composed entirely from EXISTING modules — no new backend, no new
 * primitives, no new visual grammar. Reuses Card, SectionHeader,
 * SeverityBadge, ListRow, Button, DeltaChip.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionHeader, SeverityBadge, Button, DeltaChip } from "../components/lib";
import { ListRow } from "../components/lib.overlay";
import { usePureH } from "../context/PureHContext";
import { EXECUTIVE_DASHBOARD_DATA } from "../mocks/data";
import { DECISIONS }                 from "../mocks/decisionsData";
import { BRIEFS }                    from "../mocks/briefsData";
import { AI_APPROVALS }              from "../mocks/aiData";
import { FIN_RISKS }                 from "../mocks/financeData";
import { RISKS as OPS_RISKS }        from "../mocks/operationsData";
import { PRODUCTS }                  from "../mocks/commerceData";
import { CAMPAIGNS }                 from "../mocks/marketingData";
import { BATCHES }                   from "../mocks/operationsData";
import { LAUNCH_PROJECTS }           from "../mocks/launchData";
import { OKRS }                      from "../mocks/okrsData";
import { COST_CENTERS }              from "../mocks/costCentersData";
import { PROCUREMENT }               from "../mocks/procurementData";
import { RISKS_REGISTRY }            from "../mocks/risksRegistryData";
import { MEETINGS }                  from "../mocks/meetingsData";
import { useLiveTick }               from "../lib/useLiveTick";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function OwnerCockpit() {
  const { user, currentCompany, currentWorkspace } = usePureH();
  const navigate = useNavigate();
  useLiveTick();

  // ── Phase D · Wave 2/3/4 signals ─────────────────────────────
  const okrsAtRisk = useMemo(
    () => OKRS.filter((o) => o.status === "at-risk" || o.status === "off-track"),
    [],
  );
  const hotCostCenters = useMemo(
    () => COST_CENTERS.filter((c) => c.utilizationPct >= 90),
    [],
  );
  const stuckPOs = useMemo(
    () => PROCUREMENT.filter((p) =>
      (p.status === "pending" || p.status === "draft") &&
      // heuristic: any ordered date NOT in hours == likely ≥3 days
      /\bd ago\b|\bwk ago\b|\bmo ago\b/i.test(p.orderedRelative),
    ),
    [],
  );
  const risksReopenedRecent = useMemo(
    () => RISKS_REGISTRY.filter((r) => r.status === "open"),
    [],
  );
  const meetingsToday = useMemo(
    () => MEETINGS.filter((m) => /today/i.test(m.scheduledRelative) && m.status === "scheduled"),
    [],
  );

  // ── 1 · Executive Snapshot (compact strip, first 4 KPIs) ─────
  const snapshotKpis = useMemo(
    () => (EXECUTIVE_DASHBOARD_DATA.kpis ?? []).slice(0, 4),
    [],
  );

  // ── 2 · Pending Decisions ────────────────────────────────────
  const pendingDecisions = EXECUTIVE_DASHBOARD_DATA.decisions ?? [];

  // ── 3 · Latest Board Brief ───────────────────────────────────
  const latestBrief = BRIEFS.length > 0 ? BRIEFS[0] : null;

  // ── 4 · AI Recommendations ───────────────────────────────────
  const recs = (EXECUTIVE_DASHBOARD_DATA.recommendations ?? []).slice(0, 4);

  // ── 5 · Critical Alerts ──────────────────────────────────────
  const criticalAlerts = useMemo(() => {
    const attention = (EXECUTIVE_DASHBOARD_DATA.attention ?? [])
      .filter((a) => a.severity === "high")
      .map((a) => ({
        id: `att-${a.id}`,
        kind: "attention" as const,
        title: a.title,
        detail: a.detail,
        due: a.dueRelative,
        severity: "high" as const,
        href: a.link ?? "/pureh/dashboard",
      }));
    const finRisks = FIN_RISKS
      .filter((r) => r.severity === "high" && r.status !== "mitigated")
      .map((r) => ({
        id: `fin-${r.id}`,
        kind: "finance" as const,
        title: r.title,
        detail: r.note,
        due: r.status,
        severity: "high" as const,
        href: "/pureh/finance/risks",
      }));
    const opsRisksHigh = OPS_RISKS
      .filter((r) => r.severity === "high" && r.status !== "mitigated")
      .map((r) => ({
        id: `ops-${r.id}`,
        kind: "operations" as const,
        title: r.title,
        detail: r.note,
        due: r.status,
        severity: "high" as const,
        href: "/pureh/operations/risks",
      }));
    return [...attention, ...finRisks, ...opsRisksHigh];
  }, []);

  // ── 6 · Personal Approvals (pending, owner-actionable) ───────
  const personalApprovals = useMemo(
    () => AI_APPROVALS.filter((a) => a.status === "pending"),
    [],
  );

  // ── 7 · Company Overview (composite counts, live) ────────────
  const overviewStats = useMemo(() => {
    const holdDecisions = DECISIONS.filter((d) => d.outcome === "hold" || d.outcome === "deferred").length;
    const liveCampaigns = CAMPAIGNS.filter((c) => c.status === "in-flight" || c.status === "live").length;
    const activeBatches = BATCHES.filter((b) => b.stage !== "done").length;
    const activeLaunches = LAUNCH_PROJECTS.filter((l) => l.phase !== "post-launch").length;
    return { holdDecisions, liveCampaigns, activeBatches, activeLaunches };
  }, []);

  // ── 8 · Quick Actions ────────────────────────────────────────
  const quickActions: { label: string; href: string; testId: string }[] = [
    { label: "Open Executive Dashboard", href: "/pureh/dashboard",     testId: "qa-dashboard" },
    { label: "Review all Decisions",     href: "/pureh/decisions",     testId: "qa-decisions" },
    { label: "Read latest Reports",      href: "/pureh/reports",       testId: "qa-reports"   },
    { label: "See all Scorecards",       href: "/pureh/scorecards",    testId: "qa-scorecards" },
    { label: "Approvals queue",          href: "/pureh/ai/approvals",  testId: "qa-approvals" },
    { label: "Command palette (⌘K)",     href: "/pureh/search",        testId: "qa-search"    },
  ];

  const ownerName = user?.name?.split(" ")[0] ?? "there";

  return (
    <div className="space-y-6" data-testid="owner-cockpit">
      {/* ── Executive Snapshot ── */}
      <Card testId="oc-snapshot">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>
              Owner Cockpit
            </div>
            <div className="mt-1 text-lg font-semibold" style={{ color: "var(--ph-text)" }}>
              {greeting()}, {ownerName}.
            </div>
            <div className="mt-0.5 text-xs" style={{ color: "var(--ph-muted)" }}>
              {currentCompany?.name ?? "Pure H"} · {currentWorkspace?.name ?? "Workspace"}
            </div>
          </div>
          <Button variant="secondary" onClick={() => navigate("/pureh/dashboard")} testId="oc-open-dashboard">
            Open Executive Dashboard →
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {snapshotKpis.map((k) => (
            <div key={k.key} data-testid={`oc-snapshot-${k.key}`}>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>{k.label}</div>
              <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)", letterSpacing: "-0.01em" }}>
                {k.value}
              </div>
              <div className="mt-1"><DeltaChip delta={k.delta} trend={k.trend} /></div>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Phase D signals · at-a-glance strip ── */}
      <Card testId="oc-phase-d-signals">
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>Attention today</div>
          <div className="mt-1 text-base font-semibold" style={{ color: "var(--ph-text)" }}>Phase D signals across the platform</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <button
            data-testid="signal-okrs"
            onClick={() => navigate("/pureh/okrs")}
            className="text-left p-3 rounded hover:bg-slate-100 transition-colors"
            style={{ background: "var(--ph-surface-subtle)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>OKRs at risk</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: okrsAtRisk.length > 0 ? "var(--ph-danger)" : "var(--ph-text)" }}>{okrsAtRisk.length}</div>
          </button>
          <button
            data-testid="signal-cost-centers"
            onClick={() => navigate("/pureh/cost-centers")}
            className="text-left p-3 rounded hover:bg-slate-100 transition-colors"
            style={{ background: "var(--ph-surface-subtle)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Cost centers ≥ 90%</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: hotCostCenters.length > 0 ? "var(--ph-warning)" : "var(--ph-text)" }}>{hotCostCenters.length}</div>
          </button>
          <button
            data-testid="signal-stuck-pos"
            onClick={() => navigate("/pureh/procurement")}
            className="text-left p-3 rounded hover:bg-slate-100 transition-colors"
            style={{ background: "var(--ph-surface-subtle)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>POs stuck ≥ 3 d</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: stuckPOs.length > 0 ? "var(--ph-warning)" : "var(--ph-text)" }}>{stuckPOs.length}</div>
          </button>
          <button
            data-testid="signal-risks-open"
            onClick={() => navigate("/pureh/risks")}
            className="text-left p-3 rounded hover:bg-slate-100 transition-colors"
            style={{ background: "var(--ph-surface-subtle)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Open risks</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: risksReopenedRecent.length > 0 ? "var(--ph-danger)" : "var(--ph-text)" }}>{risksReopenedRecent.length}</div>
          </button>
          <button
            data-testid="signal-meetings-today"
            onClick={() => navigate("/pureh/meetings")}
            className="text-left p-3 rounded hover:bg-slate-100 transition-colors"
            style={{ background: "var(--ph-surface-subtle)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Meetings today</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{meetingsToday.length}</div>
          </button>
        </div>
      </Card>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column — needs attention */}
        <div className="space-y-6">
          {/* Critical Alerts */}
          <Card padded={false} testId="oc-alerts">
            <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <SectionHeader
                eyebrow="Critical Alerts"
                title={`${criticalAlerts.length} high-severity item${criticalAlerts.length === 1 ? "" : "s"}`}
              />
              <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
                Anything red across the platform. Deal with these first.
              </div>
            </div>
            <div className="p-2">
              {criticalAlerts.length === 0 ? (
                <div className="p-5 text-sm" style={{ color: "var(--ph-muted)" }}>Nothing critical open.</div>
              ) : criticalAlerts.map((a) => (
                <ListRow key={a.id} testId={`oc-alert-${a.id}`}
                  onClick={() => navigate(a.href)}
                  label={
                    <span className="flex items-center gap-2">
                      <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>
                        {a.kind}
                      </span>
                      <span>{a.title}</span>
                    </span>
                  }
                  sublabel={<>{a.detail} · {a.due}</>}
                  trailing={<SeverityBadge severity={a.severity} />}
                />
              ))}
            </div>
          </Card>

          {/* Pending Decisions */}
          <Card padded={false} testId="oc-pending-decisions">
            <div className="p-5 flex items-start justify-between" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <div>
                <SectionHeader
                  eyebrow="Pending Decisions"
                  title={`${pendingDecisions.length} awaiting your call`}
                />
                <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
                  Governance calls the owner needs to make today.
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate("/pureh/decisions")} testId="oc-decisions-all">
                Decision Log →
              </Button>
            </div>
            <div className="p-2">
              {pendingDecisions.length === 0 ? (
                <div className="p-5 text-sm" style={{ color: "var(--ph-muted)" }}>No decisions pending.</div>
              ) : pendingDecisions.map((d) => (
                <ListRow key={d.id} testId={`oc-decision-${d.id}`}
                  onClick={() => navigate("/pureh/decisions")}
                  label={d.title}
                  sublabel={<>{d.owner} · {d.context} · required by {d.requiredBy}</>}
                />
              ))}
            </div>
          </Card>

          {/* Personal Approvals */}
          <Card padded={false} testId="oc-approvals">
            <div className="p-5 flex items-start justify-between" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <div>
                <SectionHeader
                  eyebrow="Personal Approvals"
                  title={`${personalApprovals.length} pending`}
                />
                <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
                  AI + human requests waiting for your yes/no.
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate("/pureh/ai/approvals")} testId="oc-approvals-all">
                Approvals →
              </Button>
            </div>
            <div className="p-2">
              {personalApprovals.length === 0 ? (
                <div className="p-5 text-sm" style={{ color: "var(--ph-muted)" }}>All caught up.</div>
              ) : personalApprovals.map((a) => (
                <ListRow key={a.id} testId={`oc-approval-${a.id}`}
                  onClick={() => navigate("/pureh/ai/approvals")}
                  label={a.title}
                  sublabel={<>Requested by {a.askedBy} · {a.askedRelative} · EGP {a.amountEGP.toLocaleString()}</>}
                  trailing={
                    <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>
                      Pending
                    </span>
                  }
                />
              ))}
            </div>
          </Card>
        </div>

        {/* Right column — context + actions */}
        <div className="space-y-6">
          {/* Latest Board Brief */}
          <Card testId="oc-latest-brief">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>
                  Latest Board Brief
                </div>
                <div className="mt-1 text-base font-semibold" style={{ color: "var(--ph-text)" }}>
                  {latestBrief?.title ?? "No brief yet"}
                </div>
              </div>
              {latestBrief && (
                <Button variant="ghost" onClick={() => navigate("/pureh/briefs")} testId="oc-brief-open">
                  Read →
                </Button>
              )}
            </div>
            {latestBrief ? (
              <>
                <div className="text-xs mb-3" style={{ color: "var(--ph-muted)" }}>
                  {latestBrief.authoredBy} · published {latestBrief.publishedRelative}
                </div>
                <div className="text-sm leading-relaxed mb-4" style={{ color: "var(--ph-text)" }}>
                  {latestBrief.headline}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {latestBrief.kpiHighlights.map((h) => (
                    <div key={h.label}>
                      <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>{h.label}</div>
                      <div className="mt-1 text-sm font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{h.value}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm" style={{ color: "var(--ph-muted)" }}>No board briefs have been published yet.</div>
            )}
          </Card>

          {/* AI Recommendations */}
          <Card padded={false} testId="oc-recommendations">
            <div className="p-5 flex items-start justify-between" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <div>
                <SectionHeader
                  eyebrow="AI Recommendations"
                  title={`${recs.length} suggested move${recs.length === 1 ? "" : "s"}`}
                />
                <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
                  Prioritised bets your AI teammates recommend acting on.
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate("/pureh/ai/insights")} testId="oc-recs-all">
                All Insights →
              </Button>
            </div>
            <div className="p-2">
              {recs.map((r) => (
                <ListRow key={r.id} testId={`oc-rec-${r.id}`}
                  onClick={() => navigate("/pureh/ai/insights")}
                  label={r.title}
                  sublabel={<>{r.action} · impact: {r.impact}</>}
                  trailing={
                    <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>
                      effort {r.effort}
                    </span>
                  }
                />
              ))}
            </div>
          </Card>

          {/* Company Overview */}
          <Card testId="oc-company-overview">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>
                  Company Overview
                </div>
                <div className="mt-1 text-base font-semibold" style={{ color: "var(--ph-text)" }}>
                  {currentCompany?.name ?? "Pure H"} · {currentWorkspace?.name ?? "Workspace"}
                </div>
                <div className="mt-0.5 text-xs" style={{ color: "var(--ph-muted)" }}>
                  Snapshot of the ORVAN operating state right now.
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate("/pureh/company")} testId="oc-company-open">
                Detail →
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div data-testid="oc-ov-products">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Live SKUs</div>
                <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>
                  {PRODUCTS.filter((p) => p.status === "live").length}
                </div>
              </div>
              <div data-testid="oc-ov-campaigns">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Campaigns live</div>
                <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{overviewStats.liveCampaigns}</div>
              </div>
              <div data-testid="oc-ov-batches">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Batches active</div>
                <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{overviewStats.activeBatches}</div>
              </div>
              <div data-testid="oc-ov-launches">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Launches in flight</div>
                <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{overviewStats.activeLaunches}</div>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card testId="oc-quick-actions">
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>Quick Actions</div>
              <div className="mt-1 text-base font-semibold" style={{ color: "var(--ph-text)" }}>Morning shortcuts</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {quickActions.map((q) => (
                <Button key={q.testId} variant="secondary" onClick={() => navigate(q.href)} testId={q.testId}>
                  {q.label}
                </Button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
